import { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle } from 'lucide-react';
import { EnrichedLifeglovLinerStock } from '../../api/lifeglov-liner-stock';
import { useAuthentication } from '../../auth';
import { SortableHeader } from '../../components/DataTable/SortableHeader';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { capitalizeFirstLetter } from '../../util';
import { LifeglovLinerStockTableActions } from '../components/LifeglovLinerStockTableActions';

export function useLifeglovLinerStockColumns() {
  const currentUser = useAuthentication();

  const isLowStock = (stock: EnrichedLifeglovLinerStock): boolean => {
    const subtenant = currentUser?.subtenants?.find((s) => s.name === stock.subtenantName);
    const threshold = subtenant?.lifeglovLinerStockLowThreshold;
    return threshold != null && stock.available <= threshold;
  };

  const columns: ColumnDef<EnrichedLifeglovLinerStock>[] = [
    {
      accessorKey: 'subtenantName',
      header: ({ column }) => <SortableHeader column={column}>Clinic</SortableHeader>,
      cell: ({ row }) => {
        const subtenantName = row.getValue('subtenantName');
        const subtenant = currentUser.subtenants?.find((s) => s.name === subtenantName);
        return subtenant?.label ?? subtenantName;
      }
    },
    {
      accessorKey: 'hand',
      header: ({ column }) => <SortableHeader column={column}>Hand</SortableHeader>,
      cell: ({ row }) => {
        return capitalizeFirstLetter(row.getValue('hand'));
      }
    },
    {
      accessorKey: 'size',
      header: ({ column }) => <SortableHeader column={column}>Size</SortableHeader>,
      cell: ({ row }) => {
        return capitalizeFirstLetter(row.getValue('size'));
      }
    },
    {
      accessorKey: 'count',
      header: ({ column }) => <SortableHeader column={column}>Shipped</SortableHeader>
    },
    {
      accessorKey: 'adjustment',
      header: ({ column }) => <SortableHeader column={column}>Adjustment</SortableHeader>
    },
    {
      accessorKey: 'used',
      header: ({ column }) => <SortableHeader column={column}>Used</SortableHeader>
    },
    {
      accessorKey: 'available',
      header: ({ column }) => <SortableHeader column={column}>Available</SortableHeader>,
      cell: ({ row }) => {
        const available = row.getValue<number>('available');
        return (
          <div className="flex items-center gap-2">
            {isLowStock(row.original) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="text-warning h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent className="flex max-w-80 items-center gap-2">
                  <span>
                    The available stock for this clinic is at or below the low stock threshold. Consider requesting a
                    refill to replenish the stock.
                  </span>
                </TooltipContent>
              </Tooltip>
            )}
            <span>{available}</span>
          </div>
        );
      },
      sortingFn: (a, b, columnId) => {
        const aLow = isLowStock(a.original);
        const bLow = isLowStock(b.original);
        if (aLow !== bLow) {
          return aLow ? -1 : 1;
        }
        return a.getValue<number>(columnId) - b.getValue<number>(columnId);
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      size: 50,
      cell: ({ row }) => <LifeglovLinerStockTableActions stock={row.original} />
    }
  ];

  return columns;
}
